import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

import AppLogoIcon from '@/components/app-logo-icon';

import { useWidgetChat } from '../hooks/use-widget-chat';
import { WidgetChatComposer } from './chat-composer';
import { WidgetChatList } from './chat-list';

const ORDERPILOT_URL = 'https://orderpilot.biz';

type WidgetRootProps = {
    widgetKey: string;
    apiOrigin: string;
    open: boolean;
    onClose: () => void;
    onUnread?: (count: number) => void;
};

function serverIdOf(id: string): number | null {
    const numeric = Number(id.replace(/^\D+/, ''));

    return Number.isSafeInteger(numeric) ? numeric : null;
}

function unreadStorageKey(widgetKey: string): string {
    return `orderpilot_unread_${widgetKey}`;
}

function readStoredSeen(widgetKey: string): number {
    try {
        const raw = window.localStorage.getItem(unreadStorageKey(widgetKey));
        const parsed = raw === null ? 0 : Number(JSON.parse(raw));

        return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 0;
    } catch {
        return 0;
    }
}

function writeStoredSeen(widgetKey: string, seen: number): void {
    try {
        window.localStorage.setItem(
            unreadStorageKey(widgetKey),
            JSON.stringify(seen),
        );
    } catch {
        // Storage unavailable (private mode) — badge just won't persist.
    }
}
function initialsFor(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
        return 'OP';
    }

    const first = parts[0]?.charAt(0) ?? 'O';
    const second = parts.length > 1 ? (parts[1]?.charAt(0) ?? '') : '';

    return `${first}${second}`.toUpperCase();
}

export function WidgetRoot({
    widgetKey,
    apiOrigin,
    open,
    onClose,
    onUnread,
}: WidgetRootProps) {
    const {
        messages,
        displayName: displayNameFromConfig,
        appearance,
        texts,
        ready,
        error,
        typing,
        send,
        retry,
        markRead,
    } = useWidgetChat({ widgetKey, apiOrigin, enabled: true });

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const seenShopId = useRef<number>(0);
    const lastSentRead = useRef<number>(0);

    useEffect(() => {
        const stored = readStoredSeen(widgetKey);
        seenShopId.current = stored;
        lastSentRead.current = stored;
    }, [widgetKey]);

    useEffect(() => {
        const shopIds = messages
            .filter((m) => m.sender === 'shop')
            .map((m) => serverIdOf(m.id))
            .filter((id): id is number => id !== null);
        const maxShopId = shopIds.length > 0 ? Math.max(...shopIds) : 0;

        if (open) {
            seenShopId.current = maxShopId;
            writeStoredSeen(widgetKey, maxShopId);

            if (maxShopId > lastSentRead.current) {
                lastSentRead.current = maxShopId;
                markRead(maxShopId);
            }

            if (onUnread) {
                onUnread(0);
            }

            return;
        }

        const unread = shopIds.filter((id) => id > seenShopId.current).length;

        if (onUnread) {
            onUnread(unread);
        }
    }, [messages, open, onUnread, widgetKey, markRead]);

    const displayName = displayNameFromConfig || 'Store chat';
    const connecting = !ready && !error;
    const unavailable = error !== null && messages.length === 0;
    const statusLabel = unavailable
        ? 'Offline'
        : connecting
          ? 'Connecting…'
          : 'Online';

    const theme = {
        ...(appearance.primaryColor
            ? { '--op-primary': appearance.primaryColor }
            : {}),
        ...(appearance.chatBg ? { '--op-card': appearance.chatBg } : {}),
    } as CSSProperties;

    useEffect(() => {
        const host = document.getElementById('orderpilot-root');

        if (!host) {
            return;
        }

        if (appearance.primaryColor) {
            host.style.setProperty('--op-primary', appearance.primaryColor);
        } else {
            host.style.removeProperty('--op-primary');
        }

        if (appearance.chatBg) {
            host.style.setProperty('--op-card', appearance.chatBg);
        } else {
            host.style.removeProperty('--op-card');
        }

        host.toggleAttribute('data-op-ready', ready || error !== null);
    }, [appearance.primaryColor, appearance.chatBg, ready, error]);

    const headerTheme = {
        ...(appearance.headerText ? { color: appearance.headerText } : {}),
    } as CSSProperties;

    const avatarLogoUrl = appearance.showLogo ? appearance.logoUrl : null;

    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const rootNode = cardRef.current?.getRootNode();

        if (rootNode instanceof ShadowRoot) {
            rootNode.host.setAttribute(
                'data-pos',
                appearance.position ?? 'right',
            );
        }
    }, [appearance.position]);

    return (
        <div className="op-card" ref={cardRef} style={theme}>
            <div className="op-header" style={headerTheme}>
                <div
                    className="op-avatar"
                    aria-hidden="true"
                    style={
                        avatarLogoUrl
                            ? { borderRadius: 0, background: 'transparent' }
                            : undefined
                    }
                >
                    {avatarLogoUrl ? (
                        <img
                            src={avatarLogoUrl}
                            alt=""
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        initialsFor(displayName)
                    )}
                </div>
                <div className="op-titles">
                    <span className="op-name">{displayName}</span>
                    <span
                        className={`op-status${connecting ? 'op-status-connecting' : ''}${unavailable ? 'op-status-offline' : ''}`}
                    >
                        <span className="op-dot" aria-hidden="true" />
                        {statusLabel}
                    </span>
                </div>
                <button
                    type="button"
                    className="op-close"
                    aria-label="Close chat"
                    onClick={onClose}
                >
                    <X aria-hidden="true" />
                </button>
            </div>
            {unavailable ? (
                <div className="op-error" role="alert">
                    <p>{error}</p>
                    <p className="op-error-hint">
                        Please try again in a moment.
                    </p>
                </div>
            ) : (
                <WidgetChatList
                    messages={messages}
                    loading={connecting && messages.length === 0}
                    typing={typing}
                    welcomeMessage={texts.welcomeMessage}
                    onRetry={retry}
                />
            )}
            <WidgetChatComposer
                disabled={!ready}
                placeholder={texts.placeholder}
                onSend={send}
            />
            <a
                className="op-credit"
                href={ORDERPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
            >
                <span>Powered by</span>
                <AppLogoIcon title="" className="op-credit-logo" />
                <span className="op-credit-name">
                    <span className="op-credit-order">Order</span>
                    <span className="op-credit-pilot">Pilot</span>
                </span>
            </a>
        </div>
    );
}
